import reactUpdate from 'immutability-helper';
import isEqual from 'react-fast-compare';

import {DataTableConfig, GenericDataTable} from 'notebook/data-tables/DataTableConstants';
import * as DataTableUtil from 'notebook/data-tables/DataTableUtil';
import {ClientId} from 'notebook/ELNConstants';
import {isMixtureGroup} from 'notebook/integrations/registry/MixtureGroupConstants';
import {NoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import NoteConstants from 'notebook/notes/NoteConstants';
import getNotePartType from 'notebook/notes/util/getNotePartType';
import {
  GenericRegistrationTable,
  RegistrationTableConfig,
} from 'notebook/registration-tables/RegistrationTableConstants';
import {
  convertRegistrationTableConfigToTable,
  convertRegistrationTableToTable,
  mergeTableUpdateIntoRegistrationTable,
  mergeTableUpdateIntoRegistrationTableConfig,
} from 'notebook/registration-tables/RegistrationTableNotePartUtil';
import excess from 'notebook/tables/excess';
import SpreadsheetEvalCache from 'notebook/tables/spreadsheets/SpreadsheetEvalCache';
import {EvaluationSnapshot} from 'notebook/tables/TableStoreTransforms';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {TransferTableConfig} from 'notebook/transfer-tables/TransferTableConstants';
import {TransferTableForConvertToTable} from 'notebook/transfer-tables/TransferTableNotePartUtil';
import {AnyStructuredTable, NoteLinkedObjectType, NotePart} from 'notebook/util/NoteUtilConstants';
import {assertNever} from 'util/Types';
import {StageRun} from 'workflows/WorkflowConstants';

import {FullTableMap, OutputTableMap} from './excess/Evaluator';
import {Cells, Table} from './TableConstants';

export const {TABLE} = NoteConstants;

interface TableOps {
  [tableType: string]: {
    extractTable(
      notePart: NotePart,
      fileId: string,
      // TODO(BNCH-13229): Confirm stageRuns does not affect correctness and remove entirely.
      stageRuns: Array<StageRun>
    ): Table;

    mergeTable(notePart: NotePart, table: Table): NotePart;
    provenanceUuids(notePart: NotePart): Array<string>;
  };
}

/**
 * Uses the provenance ID of the note part to create a unique UUID for a plain table that can interaoperate
 * with other table provenance UUIDs.
 */
export function generatePlainTableProvenanceUuid(notePartProvenanceId: number): string {
  return `plntable-0000-0000-0000-${notePartProvenanceId.toString().padStart(12, '0')}`;
}

export const TABLE_OPS: TableOps = {
  [TABLE]: {
    extractTable(notePart: NotePart): Table {
      return notePart.table!;
    },
    mergeTable(notePart: NotePart, table: Table): NotePart {
      return reactUpdate(notePart, {$merge: {table}});
    },
    provenanceUuids(notePart: NotePart): Array<string> {
      if (!notePart.provenanceId) {
        return [];
      }
      return notePart.provenanceId != null ? [generatePlainTableProvenanceUuid(notePart.provenanceId)] : [];
    },
  },
  [NoteLinkedObjectType.DATA_TABLE]: {
    extractTable(notePart: NotePart): Table {
      const dataTable = notePart.item as GenericDataTable<ClientId>;
      return DataTableUtil.convertDataTableToTable(dataTable);
    },
    mergeTable(notePart: NotePart, table: Table): NotePart {
      return reactUpdate(notePart, {
        item: {
          $merge: DataTableUtil.mergeTableUpdateIntoDataTable(
            notePart.item as GenericDataTable<ClientId>,
            table
          ),
        },
      });
    },
    provenanceUuids(notePart: NotePart): Array<string> {
      const dataTable = notePart.item as GenericDataTable<ClientId>;
      return dataTable.provenanceUuid != null ? [dataTable.provenanceUuid] : [];
    },
  },
  [NoteLinkedObjectType.REGISTRATION_TABLE]: {
    extractTable(notePart: NotePart, fileId: string, stageRuns: Array<StageRun>): Table {
      const registrationTable = notePart.item as GenericRegistrationTable<ClientId>;
      return convertRegistrationTableToTable(registrationTable, fileId, stageRuns);
    },
    mergeTable(notePart: NotePart, table: Table): NotePart {
      return reactUpdate(notePart, {
        item: {
          $merge: mergeTableUpdateIntoRegistrationTable(
            notePart.item as GenericRegistrationTable<ClientId>,
            table
          ),
        },
      });
    },
    provenanceUuids(notePart: NotePart): Array<string> {
      return [(notePart.item as GenericRegistrationTable<ClientId>).provenanceUuid];
    },
  },
  [NoteLinkedObjectType.STRUCTURED_TABLE]: {
    extractTable(notePart: NotePart, _fileId: string): Table {
      // Avoid circular dependency introduced by StructuredTableAdapters that can cause
      // NoteStore.resetState (which is called during module initialization) to reference exports from
      // NoteStoreTransforms before that module has been initialized.
      const {getStructuredTableAdapters} = require('notebook/structured-tables/StructuredTableAdapters');
      const structuredTable = notePart.item as AnyStructuredTable;
      const structuredTableAdapter = getStructuredTableAdapters()[structuredTable.kind];
      return structuredTableAdapter.toTable(structuredTable);
    },
    mergeTable(notePart: NotePart): NotePart {
      // StructuredTables don't need cachedTypedValue / cachedValue.
      return notePart;
    },
    provenanceUuids(notePart: NotePart): Array<string> {
      return [(notePart.item as AnyStructuredTable).provenanceUuid];
    },
  },
  [NoteLinkedObjectType.TRANSFER_TABLE]: {
    extractTable(notePart: NotePart, _fileId: string, stageRuns: Array<StageRun>): Table {
      const {convertTransferTableToTable} = require('notebook/transfer-tables/TransferTableNotePartUtil');
      return convertTransferTableToTable(notePart.item, stageRuns);
    },
    mergeTable(notePart: NotePart, table: Table): NotePart {
      const {
        mergeTableUpdateIntoTransferTable,
      } = require('notebook/transfer-tables/TransferTableNotePartUtil');
      return reactUpdate(notePart, {
        item: {$merge: mergeTableUpdateIntoTransferTable(notePart.item, table)},
      });
    },
    provenanceUuids(notePart: NotePart): Array<string> {
      const transferTable = notePart.item as TransferTableForConvertToTable;
      return transferTable.provenanceUuid != null ? [transferTable.provenanceUuid] : [];
    },
  },
  [NoteLinkedObjectType.DATA_TABLE_CONFIG]: {
    extractTable(notePart: NotePart, _fileId: string, stageRuns: Array<StageRun>): Table {
      const dataTableConfig = notePart.item as DataTableConfig;
      return DataTableUtil.convertDataTableConfigToTable(dataTableConfig, true);
    },
    mergeTable(notePart: NotePart, table: Table): NotePart {
      return reactUpdate(notePart, {
        item: {
          $merge: DataTableUtil.mergeTableUpdateIntoDataTableConfig(notePart.item as DataTableConfig, table),
        },
      });
    },
    provenanceUuids(notePart: NotePart): Array<string> {
      const dataTableConfig = notePart.item as DataTableConfig;
      return dataTableConfig.provenanceUuid != null ? [dataTableConfig.provenanceUuid] : [];
    },
  },
  [NoteLinkedObjectType.REGISTRATION_TABLE_CONFIG]: {
    extractTable(notePart: NotePart, fileId: string, stageRuns: Array<StageRun>): Table {
      const registrationTableConfig = notePart.item as RegistrationTableConfig;
      return convertRegistrationTableConfigToTable(registrationTableConfig, fileId);
    },
    mergeTable(notePart: NotePart, table: Table): NotePart {
      return reactUpdate(notePart, {
        item: {
          $merge: mergeTableUpdateIntoRegistrationTableConfig(
            notePart.item as RegistrationTableConfig,
            table
          ),
        },
      });
    },
    provenanceUuids(notePart: NotePart): Array<string> {
      return [(notePart.item as RegistrationTableConfig).provenanceUuid];
    },
  },
  [NoteLinkedObjectType.TRANSFER_TABLE_CONFIG]: {
    extractTable(notePart: NotePart, _fileId: string, stageRuns: Array<StageRun>): Table {
      const {createTableForConfig} = require('notebook/transfer-tables/TransferTableUtil');
      const transferTableConfig = notePart.item as TransferTableConfig;
      const {name, columns, containerSchema, parentStorableIsGrid, provenanceUuid} = transferTableConfig;
      return createTableForConfig(name, columns, containerSchema, parentStorableIsGrid, provenanceUuid);
    },
    mergeTable(notePart: NotePart, table: Table): NotePart {
      const {
        mergeTableUpdateIntoTransferTableConfig,
      } = require('notebook/transfer-tables/TransferTableNotePartUtil');
      return reactUpdate(notePart, {
        item: {
          $merge: mergeTableUpdateIntoTransferTableConfig(notePart.item as TransferTableConfig, table),
        },
      });
    },
    provenanceUuids(notePart: NotePart): Array<string> {
      const transferTableConfig = notePart.item as TransferTableConfig;
      return transferTableConfig.provenanceUuid != null ? [transferTableConfig.provenanceUuid] : [];
    },
  },
};

export function extractTables(
  noteParts: Array<NotePart>,
  fileId: string,
  stageRuns: Array<StageRun>,
  options: {includeNoteLinkedGroups: boolean} = {
    includeNoteLinkedGroups: false,
  }
): {tables: Array<Table | null>; fullTableMap: FullTableMap} {
  const fullTableMap: FullTableMap = {};

  function extractTable(notePart: NotePart): Table {
    return TABLE_OPS[getNotePartType(notePart)!].extractTable(notePart, fileId, stageRuns);
  }

  const tables = noteParts.map((notePart) => {
    const type = getNotePartType(notePart);
    if (type == null) {
      return null;
    }
    // Extract the tables within a mixture prep group
    if (type === NoteLinkedObjectType.NOTE_LINKED_GROUP && options.includeNoteLinkedGroups) {
      const noteLinkedGroup = notePart.item as NoteLinkedGroup<unknown>;
      if (!isMixtureGroup(noteLinkedGroup)) {
        return null;
      }
      const containedTables = noteLinkedGroup.containedNoteParts
        .filter((notePart) => {
          const notePartType = getNotePartType(notePart);
          return notePartType != null && TABLE_OPS[notePartType];
        })
        .map((notePart) => extractTable(notePart));
      for (const table of containedTables) {
        fullTableMap[table.name] = table;
      }
      return null;
    }
    if (!TABLE_OPS[type]) {
      return null;
    }

    const table = extractTable(notePart);
    fullTableMap[table.name] = table;
    return table;
  });

  return {tables, fullTableMap};
}

function augmentFromComputedTables(
  noteParts: Array<NotePart>,
  tables: Array<Table | null>,
  computedTables: OutputTableMap
): Array<NotePart> {
  // Merge the values into the tables.
  const tablesWithValues = tables.map((table) => {
    if (!table) {
      return null;
    }
    const computedTable = computedTables[table.name];
    return reactUpdate(table, {
      table: {
        $apply: (rows: Cells): Cells =>
          rows.map((row, i) =>
            row.map((cell, j) => ({
              ...cell,
              cachedValue: formatTypedValue(computedTable[i][j].value, cell),
              cachedTypedValue: computedTable[i][j].value,
            }))
          ),
      },
    });
  });

  // Merge the tables into the noteParts
  return noteParts.map((notePart, i) => {
    const type = getNotePartType(notePart);
    if (type == null) {
      return notePart;
    }
    const table = tablesWithValues[i];
    if (!table) {
      return notePart;
    }
    return TABLE_OPS[type].mergeTable(notePart, table);
  });
}

function augmentFromLiveEval(
  noteParts: Array<NotePart>,
  fileId: string,
  stageRuns: Array<StageRun>
): Array<NotePart> {
  const {tables, fullTableMap} = extractTables(noteParts, fileId, stageRuns);

  let computedTables: OutputTableMap;
  if (isEqual(fullTableMap, SpreadsheetEvalCache.getCachedTableMap(fileId))) {
    computedTables = SpreadsheetEvalCache.getCachedComputedTables(fileId)!;
  } else {
    // Evaluate cells
    const evaluator = new excess.SpreadsheetEvaluator({
      fullTableMap,
      selectors: [],
      parse: excess.parse,
      fileId,
      multidocTableOp: {[fileId]: 'eval-document'},
    });
    computedTables = evaluator.output();
  }

  return augmentFromComputedTables(noteParts, tables, computedTables);
}

function augmentFromEvalSnapshot(
  noteParts: Array<NotePart>,
  fileId: string,
  evaluationSnapshot: EvaluationSnapshot
): Array<NotePart> {
  const {tables} = extractTables(
    noteParts,
    fileId,
    // TODO(BNCH-13229): remove this hack after removing stageRuns from stuff like convertRegistrationTableToTable
    [] /* stageRuns */
  );

  return augmentFromComputedTables(noteParts, tables, evaluationSnapshot /* computedTables */);
}

export default function augmentNotePartTablesWithValues(
  noteParts: Array<NotePart>,
  fileId: string,
  sourceForValues:
    | {
        sourceType: 'LIVE_EVALUATION';
        stageRuns: Array<StageRun>;
      }
    | {sourceType: 'EVALUATION_SNAPSHOT'; evaluationSnapshot: EvaluationSnapshot}
): Array<NotePart> {
  switch (sourceForValues.sourceType) {
    case 'LIVE_EVALUATION':
      return augmentFromLiveEval(noteParts, fileId, sourceForValues.stageRuns);
    case 'EVALUATION_SNAPSHOT':
      return augmentFromEvalSnapshot(noteParts, fileId, sourceForValues.evaluationSnapshot);
    default:
      throw new Error(`Unexpected source type ${assertNever(sourceForValues).sourceType}`);
  }
}
